import type { AgentEventDisplay, AgentPendingApprovalPayload } from '@tabbio/types';
import type { JSONObject, JSONValue } from '@/lib/json';
import { normalizeJson } from '@/lib/json';
import { splitToolName } from '@/lib/tool-name';
import { stripInternalAgentAttachmentText } from '@tabbio/utils/agent-chat-attachments';
import { historyMessageAttachments, type MessageAttachmentLike } from '@/lib/agent-attachments';

type RawMessage = {
  id?: string;
  role: string;
  content: unknown;
  createdAt?: string | Date;
  feedback?: 'up' | 'down' | null;
  /** User messages: the images and PDFs sent, with 15-minute signed links. */
  attachments?: unknown;
};

type RawToolCall = {
  id?: string | null;
  toolName: string;
  args?: JSONObject;
  result?: unknown;
  /** Action-log status: 'completed' | 'failed'. */
  status?: string;
  createdAt?: string | Date;
  /** Set on a call still waiting on the user's approval (`pendingApprovals`). */
  approval?: AgentPendingApprovalPayload;
};

type ConvertedContentPart =
  | {
      type: 'text';
      text: string;
    }
  | {
      type: 'tool-call';
      toolCallId: string;
      toolName: string;
      args: JSONObject;
      result?: JSONValue;
      stage: 'completed' | 'failed' | 'pending_approval';
      isError?: boolean;
      category?: string;
      entityType?: string;
      /** A call waiting on the user: what the card's Approve and Reject act on. */
      approvalId?: string;
      entityId?: string;
      display?: AgentEventDisplay;
      cardType?: string;
      runId?: string;
      status?: string;
    };

/**
 * Rebuild the fields the live stream carries but the history endpoint does not.
 *
 * A reopened thread should be indistinguishable from the one the user watched.
 * It used to hardcode `stage: 'completed'` on every rehydrated call, so a tool
 * that had failed came back looking like it had worked - and now that each call
 * is narrated as a step, that difference is a sentence of the conversation
 * changing its meaning on reload.
 *
 * `category` and `entityType` are pure functions of the tool name on the server
 * too (`inferCategory` / `inferEntityType` in agent-stream-event.ts), so they
 * are recomputed here rather than round-tripped. `cardType` deliberately is not:
 * card selection goes through `CARD_MAP[toolName]`, and the only consumer of
 * `cardType` is the loading skeleton, which history never shows.
 */
function toolCallPart(toolCall: RawToolCall, index: number): ConvertedContentPart {
  const failed = toolCall.status === 'failed';
  const [scope] = splitToolName(toolCall.toolName);

  // Still waiting on the user: the part the live `tool-call-approval` event
  // builds (createMastraChatModel's upsertToolPart), so the card offers
  // Approve and Reject exactly as it did before the reload.
  const approval = toolCall.approval;
  if (approval) {
    return {
      type: 'tool-call',
      toolCallId: approval.id,
      toolName: approval.name,
      args: normalizeJson(approval.args ?? {}) as JSONObject,
      stage: 'pending_approval',
      approvalId: approval.approvalId,
      category: approval.category ?? (scope || undefined),
      entityType: approval.entityType ?? (scope || 'tool'),
      ...(approval.entityId ? { entityId: approval.entityId } : {}),
      ...(approval.display ? { display: approval.display } : {}),
      ...(approval.cardType ? { cardType: approval.cardType } : {}),
      ...(approval.runId ? { runId: approval.runId } : {}),
      status: 'pending',
    };
  }

  return {
    type: 'tool-call',
    toolCallId: toolCall.id ?? `log-${index}`,
    toolName: toolCall.toolName,
    args: normalizeJson(toolCall.args ?? {}) as JSONObject,
    result: toolCall.result === undefined ? undefined : normalizeJson(toolCall.result),
    stage: failed ? 'failed' : 'completed',
    ...(failed ? { isError: true } : {}),
    category: scope || undefined,
    entityType: scope || 'tool',
  };
}

export type ConvertedThreadMessage = {
  id?: string;
  role: 'user' | 'assistant';
  content: ConvertedContentPart[];
  /** User messages: what was attached, shown above the words (`lib/agent-attachments`). */
  attachments?: MessageAttachmentLike[];
  metadata?: { custom: Record<string, unknown> };
};

const TIME_BUFFER_MS = 5000;

function getTimestamp(value: string | Date | undefined): number {
  if (!value) return 0;
  const timestamp = new Date(value).getTime();
  return Number.isNaN(timestamp) ? 0 : timestamp;
}

export function extractTextFromContent(content: unknown): string {
  if (!content) return '';

  if (
    typeof content === 'object' &&
    content !== null &&
    'format' in content &&
    (content as { format?: number }).format === 2 &&
    'parts' in content &&
    Array.isArray((content as { parts?: unknown[] }).parts)
  ) {
    return ((content as { parts: { type?: string; text?: string }[] }).parts ?? [])
      .filter((part) => part.type === 'text')
      .map((part) => stripInternalAgentAttachmentText(part.text || ''))
      .join('');
  }

  if (typeof content === 'string') {
    return stripInternalAgentAttachmentText(content);
  }

  if (Array.isArray(content)) {
    return content
      .filter(
        (part): part is { type?: string; text?: string } =>
          typeof part === 'object' && part !== null,
      )
      .filter((part) => part.type === 'text')
      .map((part) => stripInternalAgentAttachmentText(part.text || ''))
      .join('');
  }

  return '';
}

export function convertToThreadMessages(
  rawMessages: RawMessage[],
  toolCalls: RawToolCall[] = [],
  threadId?: string,
  pendingApprovals: AgentPendingApprovalPayload[] = [],
): ConvertedThreadMessage[] {
  const sortedTools = [
    ...toolCalls,
    ...pendingApprovals.map(
      (approval): RawToolCall => ({
        id: approval.id,
        toolName: approval.name,
        args: approval.args as JSONObject,
        status: 'pending',
        createdAt: approval.createdAt,
        approval,
      }),
    ),
  ].sort((a, b) => getTimestamp(a.createdAt) - getTimestamp(b.createdAt));

  const filtered = rawMessages.filter(
    (message): message is RawMessage & { role: 'user' | 'assistant' } =>
      message.role === 'user' || message.role === 'assistant',
  );
  const { byMessage: toolsByMessage, ownTurns } = assignToolCalls(filtered, sortedTools);

  const converted = filtered.map((message, messageIndex) => {
    const text = extractTextFromContent(message.content);

    const parts: ConvertedContentPart[] = [];

    if (message.role === 'assistant') {
      for (const { toolCall, index } of toolsByMessage.get(messageIndex) ?? []) {
        parts.push(toolCallPart(toolCall, index));
      }
    }

    if (text.length > 0) {
      parts.push({ type: 'text', text });
    }

    // What the user attached: the history's signed links, or the local
    // preview of a message just sent while the history has none yet.
    const attachments =
      message.role === 'user' ? historyMessageAttachments(message.attachments, threadId, text) : [];

    return {
      id: message.id,
      role: message.role,
      content: parts.length > 0 ? parts : [{ type: 'text' as const, text: '' }],
      ...(attachments.length > 0 ? { attachments } : {}),
      ...(message.role === 'assistant'
        ? {
            metadata: {
              custom: {
                messageId: message.id,
                feedback: message.feedback ?? null,
                threadId,
              },
            },
          }
        : {}),
    };
  });

  // An approval asked in a turn that saved no assistant row (the run stopped
  // to wait) gets that row back: right after its user message, never on an
  // earlier turn's answer.
  const withOwnTurns = converted.flatMap((message, messageIndex) => {
    const pending = ownTurns.get(messageIndex);
    return pending ? [message, approvalTurn(pending)] : [message];
  });
  const opening = ownTurns.get(-1);
  if (opening) withOwnTurns.unshift(approvalTurn(opening));

  const meaningful = withOwnTurns.filter(
    (message) =>
      (message.attachments?.length ?? 0) > 0 ||
      message.content.some(
        (part) => part.type === 'tool-call' || (part.type === 'text' && part.text.length > 0),
      ),
  );

  return mergeConsecutiveAssistantMessages(meaningful);
}

type PlacedToolCall = { toolCall: RawToolCall; index: number };

/**
 * The assistant turn an approval opens when its own turn saved no row. Marked
 * `approvalTurn`: there is nothing to copy, rate or regenerate yet, so the
 * action bars leave it alone.
 */
function approvalTurn(placed: PlacedToolCall[]): ConvertedThreadMessage {
  return {
    role: 'assistant',
    content: placed.map(({ toolCall, index }) => toolCallPart(toolCall, index)),
    metadata: { custom: { approvalTurn: true } },
  };
}

/**
 * Which assistant message each tool call belongs to. A call belongs to the
 * turn of the last user message sent before it: the first assistant message
 * after that user message (the turn's rows merge later). The call's log is
 * written when the call ends, which for a streamed build can be long after
 * the assistant row's own time, so a window around the assistant row put a
 * page's card on the next turn once the thread had one (user test fix,
 * 2026-09-25). Without usable times the old rule applies: the first
 * assistant row whose time plus 5 seconds covers the call, the rest on the
 * last assistant row.
 *
 * A pending approval whose turn has no assistant row yet (the run stopped to
 * wait for the user) is returned in `ownTurns`, keyed by the index of its user
 * message (-1 before any): its card opens a turn of its own there.
 */
function assignToolCalls(
  messages: readonly (RawMessage & { role: 'user' | 'assistant' })[],
  sortedTools: readonly RawToolCall[],
): { byMessage: Map<number, PlacedToolCall[]>; ownTurns: Map<number, PlacedToolCall[]> } {
  const byMessage = new Map<number, PlacedToolCall[]>();
  const ownTurns = new Map<number, PlacedToolCall[]>();
  const add = (into: Map<number, PlacedToolCall[]>, key: number, toolCall: RawToolCall, index: number) => {
    const list = into.get(key) ?? [];
    list.push({ toolCall, index });
    into.set(key, list);
  };
  const assistants = messages.flatMap((message, index) => (message.role === 'assistant' ? [index] : []));
  const lastAssistant = assistants[assistants.length - 1];
  const timed =
    messages.every((message) => getTimestamp(message.createdAt) > 0) &&
    sortedTools.every((toolCall) => getTimestamp(toolCall.createdAt) > 0);
  const lastUserBefore = (at: number) => {
    let userIndex = -1;
    messages.forEach((message, messageIndex) => {
      if (message.role === 'user' && getTimestamp(message.createdAt) <= at) userIndex = messageIndex;
    });
    return userIndex;
  };

  sortedTools.forEach((toolCall, index) => {
    const at = getTimestamp(toolCall.createdAt);
    if (timed) {
      const userIndex = lastUserBefore(at);
      const target = assistants.find((messageIndex) => messageIndex > userIndex);
      if (toolCall.approval) {
        // Only an answer in the approval's own turn, before the next user
        // message: a later turn's answer is not where the user was asked.
        const nextUser = messages.findIndex((message, i) => i > userIndex && message.role === 'user');
        if (target === undefined || (nextUser !== -1 && target > nextUser)) {
          add(ownTurns, userIndex, toolCall, index);
          return;
        }
      }
      if (target !== undefined || lastAssistant !== undefined) add(byMessage, target ?? lastAssistant!, toolCall, index);
      return;
    }
    if (lastAssistant === undefined) {
      // Nothing to attach to: an approval still gets its card, after the last user message.
      if (toolCall.approval) add(ownTurns, messages.length - 1, toolCall, index);
      return;
    }
    const target =
      assistants.find((messageIndex) => at <= getTimestamp(messages[messageIndex]!.createdAt) + TIME_BUFFER_MS) ??
      lastAssistant;
    add(byMessage, target, toolCall, index);
  });
  return { byMessage, ownTurns };
}

/**
 * A single assistant turn is persisted as one row per step, so a turn that calls a
 * tool hydrates as a tool-call message followed by a final text message. The live
 * stream emits that same turn as one message, so without this the bubble (avatar,
 * header, copy/regenerate actions) is duplicated once history is refetched.
 *
 * Tool calls are ordered ahead of text within a merged turn to match the ordering
 * the streaming path produces, so a refetch doesn't visibly reshuffle the bubble.
 */
function mergeConsecutiveAssistantMessages(
  messages: ConvertedThreadMessage[],
): ConvertedThreadMessage[] {
  const merged: ConvertedThreadMessage[] = [];

  for (const message of messages) {
    const previous = merged[merged.length - 1];

    if (message.role === 'assistant' && previous?.role === 'assistant') {
      previous.content = orderTurnParts([...previous.content, ...message.content]);
      // A turn that calls a tool persists as multiple assistant DB rows (a
      // tool-call step, then a final text step). Keep the LAST row's id/
      // metadata, matching the backend's own "take the last new assistant
      // message" convention for the same turn (agent.controller.ts), so the
      // merged bubble's feedback control keys to the row actually carrying
      // the final visible text.
      previous.id = message.id;
      previous.metadata = message.metadata;
      continue;
    }

    merged.push({
      id: message.id,
      role: message.role,
      content: [...message.content],
      ...(message.attachments ? { attachments: message.attachments } : {}),
      metadata: message.metadata,
    });
  }

  return merged;
}

function orderTurnParts(parts: ConvertedContentPart[]): ConvertedContentPart[] {
  return [
    ...parts.filter((part) => part.type === 'tool-call'),
    ...parts.filter((part) => part.type === 'text'),
  ];
}
